import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ImageViewerProvider — a single, app-level fullscreen image viewer.
 *
 * Why this exists: previously every <ImageWithPreview> owned its own `isOpen`
 * state inside the Yap card. When the feed re-rendered (timestamp ticks, refresh
 * polling, re-sort) the card subtree re-rendered/remounted and the lightbox
 * state reset to closed — so the viewer popped shut after ~1s. Lifting the state
 * to a provider mounted ABOVE the routed screens means feed re-renders never
 * touch it: the viewer stays open until the user closes it.
 *
 * Cards/grids just call `useImageViewer().open(images, index)`. The viewer
 * renders once (via portal) with zoom/pan, prev/next arrows for multi-image
 * posts, and a "i / n" page indicator.
 */
interface ImageViewerContextValue {
  open: (images: string[], index?: number) => void;
}

const ImageViewerContext = createContext<ImageViewerContextValue>({ open: () => {} });

export const useImageViewer = () => useContext(ImageViewerContext);

export function ImageViewerProvider({ children }: { children: React.ReactNode }) {
  const [images, setImages] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // ── Zoom / pan state (mirrors the old ImageWithPreview lightbox) ──────────────
  const [scale, setScaleState] = useState(1);
  const [offset, setOffsetState] = useState({ x: 0, y: 0 });
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });

  const setScale = (v: number) => { scaleRef.current = v; setScaleState(v); };
  const setOffset = (v: { x: number; y: number }) => { offsetRef.current = v; setOffsetState(v); };

  // Touch tracking (never trigger re-renders)
  const lastDistance = useRef<number | null>(null);
  const dragStartTouch = useRef({ x: 0, y: 0 });
  const dragStartOffset = useRef({ x: 0, y: 0 });
  const isPinching = useRef(false);
  const isDragging = useRef(false);
  const lastTapTime = useRef(0);

  const resetZoom = useCallback(() => {
    scaleRef.current = 1;
    offsetRef.current = { x: 0, y: 0 };
    setScaleState(1);
    setOffsetState({ x: 0, y: 0 });
    lastDistance.current = null;
    isDragging.current = false;
    isPinching.current = false;
  }, []);

  const open = useCallback((imgs: string[], i = 0) => {
    const list = (imgs || []).filter(Boolean);
    if (list.length === 0) return;
    setImages(list);
    setIndex(Math.max(0, Math.min(i, list.length - 1)));
    resetZoom();
    setIsOpen(true);
  }, [resetZoom]);

  const close = useCallback(() => { resetZoom(); setIsOpen(false); }, [resetZoom]);

  const goTo = useCallback((i: number) => {
    setIndex(prev => {
      if (i < 0 || i >= images.length) return prev;
      resetZoom();
      return i;
    });
  }, [images.length, resetZoom]);

  // ── Keyboard: Esc closes, ←/→ navigate ────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') goTo(index - 1);
      else if (e.key === 'ArrowRight') goTo(index + 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, index, close, goTo]);

  // ── Touch handlers ────────────────────────────────────────────────────────────
  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 2) {
      isPinching.current = true;
      isDragging.current = false;
      lastDistance.current = getTouchDistance(e.touches);
    } else if (e.touches.length === 1) {
      isPinching.current = false;
      isDragging.current = true;
      dragStartTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      dragStartOffset.current = { ...offsetRef.current };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.touches.length === 2 && isPinching.current && lastDistance.current !== null) {
      const newDist = getTouchDistance(e.touches);
      const ratio = newDist / lastDistance.current;
      const newScale = Math.min(Math.max(scaleRef.current * ratio, 1), 5);
      setScale(newScale);
      lastDistance.current = newDist;
      if (newScale <= 1) setOffset({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && isDragging.current && scaleRef.current > 1) {
      const dx = e.touches[0].clientX - dragStartTouch.current.x;
      const dy = e.touches[0].clientY - dragStartTouch.current.y;
      setOffset({ x: dragStartOffset.current.x + dx, y: dragStartOffset.current.y + dy });
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length < 2) { isPinching.current = false; lastDistance.current = null; }
    if (e.touches.length === 0) {
      isDragging.current = false;
      if (scaleRef.current <= 1) setOffset({ x: 0, y: 0 });
    }
  };

  // ── Mouse handlers (desktop pan) ──────────────────────────────────────────────
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scaleRef.current <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    dragStartTouch.current = { x: e.clientX, y: e.clientY };
    dragStartOffset.current = { ...offsetRef.current };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || scaleRef.current <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - dragStartTouch.current.x;
    const dy = e.clientY - dragStartTouch.current.y;
    setOffset({ x: dragStartOffset.current.x + dx, y: dragStartOffset.current.y + dy });
  };
  const handleMouseUp = () => { isDragging.current = false; };

  // Double-tap / double-click to zoom in/out
  const handleTap = () => {
    const now = Date.now();
    if (now - lastTapTime.current < 300) {
      if (scaleRef.current > 1) resetZoom();
      else { setScale(2.5); setOffset({ x: 0, y: 0 }); }
    }
    lastTapTime.current = now;
  };

  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;
  const multi = images.length > 1;

  // Inline styles for the overlay controls so positioning never depends on
  // Tailwind class generation or flex/stacking quirks of the backdrop.
  const arrowBaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 100001,
    width: 44,
    height: 44,
    borderRadius: '9999px',
    background: 'rgba(255,255,255,0.9)',
    color: '#111',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
    cursor: 'pointer',
    border: 'none',
  };

  return (
    <ImageViewerContext.Provider value={{ open }}>
      {children}
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ zIndex: 2147483647, isolation: 'isolate' }}
              className="fixed inset-0 flex items-center justify-center bg-black/95 backdrop-blur-sm"
              onClick={(e) => {
                if (scaleRef.current <= 1 && e.target === e.currentTarget) close();
              }}
            >
              {/* Zoom / pan container */}
              <div
                className="relative flex items-center justify-center select-none"
                style={{
                  transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
                  transformOrigin: 'center center',
                  touchAction: 'none',
                  cursor: scale > 1 ? (isDragging.current ? 'grabbing' : 'grab') : 'default',
                  willChange: 'transform',
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onClick={handleTap}
              >
                <motion.img
                  key={index}
                  initial={{ scale: 0.92, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  src={images[index]}
                  alt={`Image ${index + 1}`}
                  draggable={false}
                  className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl pointer-events-none select-none"
                />
              </div>

              {/* Close */}
              <button
                onClick={(e) => { e.stopPropagation(); close(); }}
                aria-label="Close image"
                style={{
                  position: 'absolute', top: 16, right: 16, zIndex: 100001,
                  width: 44, height: 44, borderRadius: '9999px',
                  background: 'rgba(0,0,0,0.6)', border: '2px solid #4478ea',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', cursor: 'pointer',
                }}
              >
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>

              {/* Prev / Next — only for multi-image posts; hidden at the bounds */}
              {multi && hasPrev && (
                <button
                  onClick={(e) => { e.stopPropagation(); goTo(index - 1); }}
                  aria-label="Previous image"
                  style={{ ...arrowBaseStyle, left: 12 }}
                >
                  <ChevronLeft className="w-6 h-6" strokeWidth={2.5} />
                </button>
              )}
              {multi && hasNext && (
                <button
                  onClick={(e) => { e.stopPropagation(); goTo(index + 1); }}
                  aria-label="Next image"
                  style={{ ...arrowBaseStyle, right: 12 }}
                >
                  <ChevronRight className="w-6 h-6" strokeWidth={2.5} />
                </button>
              )}

              {/* Page indicator */}
              {multi && (
                <div
                  style={{
                    position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                    zIndex: 100001, padding: '4px 12px', borderRadius: '9999px',
                    background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 14, fontWeight: 600,
                    letterSpacing: '0.03em', pointerEvents: 'none', whiteSpace: 'nowrap',
                  }}
                >
                  {index + 1} / {images.length}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </ImageViewerContext.Provider>
  );
}

export default ImageViewerProvider;
